import React, { useMemo } from 'react';
import { Input, Select, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { ze } from 'utils/zhEn';
import { connect } from 'react-redux';
import { StackUtil } from 'utils/stack';
import { makeDetailObjectFromStackLine } from 'utils/helper';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import uniqBy from 'lodash.uniqby';

const AiIssueAnalysisAddCode = ({
  reduxState,
  popupContainer,
  codeInfoList = [],
  onCodeInfoListChange,
}) => {
  // 如果没有数据，初始化一个空的 codeInfo
  const displayCodeInfoList = codeInfoList.length > 0 ? codeInfoList : [{ line: '', codeText: '' }];
  const currentApp = useMemo(() => reduxState.app.get('current').toJS(), [reduxState.app.get('current')]);
  const { appId, pid } = currentApp;
  const currentIssueObj = useMemo(() => reduxState.issue.get('current').toJS(), [reduxState.issue.get('current')]) || {};
  const { crashDoc, isShowingMinidumpStack } = currentIssueObj;
  const { crashMap } = crashDoc || {};
  const { retraceCrashDetail, crashExceptionType } = crashMap;
  const exceptionCategory = ExceptionCategoryUtil.fromExceptionTypeInt(crashExceptionType);

  const stackLineObjList = useMemo(() => {
    return StackUtil.splitStackIntoLines(retraceCrashDetail, pid)
      .map((line) => makeDetailObjectFromStackLine(line, exceptionCategory, pid, { isMinidumpStack: isShowingMinidumpStack }))
      .filter(line => line.line);
  }, [retraceCrashDetail, pid, exceptionCategory, isShowingMinidumpStack]);

  const stackLineOptions = useMemo(() => {
    const beforeUniq = stackLineObjList.map((item) => ({ label: item.line, value: item.line }));
    return uniqBy(beforeUniq, x => x.value);
  }, [stackLineObjList]);

  // 添加新的代码片段（在指定位置后面添加）
  function handleAddCodeInfo(insertIndex) {
    const newCodeInfo = {
      line: '',
      codeText: '',
    };

    // 直接基于当前显示的数据进行操作
    const currentList = codeInfoList.length > 0 ? codeInfoList : displayCodeInfoList;
    const newList = [...currentList];
    newList.splice(insertIndex + 1, 0, newCodeInfo);

    if (onCodeInfoListChange) {
      onCodeInfoListChange(newList);
    }
  }

  // 删除代码片段
  function handleDeleteCodeInfo(index) {
    // 直接基于当前显示的数据进行操作
    const currentList = codeInfoList.length > 0 ? codeInfoList : displayCodeInfoList;
    const newList = currentList.filter((_, i) => i !== index);
    if (onCodeInfoListChange) {
      onCodeInfoListChange(newList);
    }
  }

    // 更新选择的堆栈行
  function handleLineChange(index, value) {
    // 直接基于当前显示的数据进行操作
    const currentList = codeInfoList.length > 0 ? codeInfoList : displayCodeInfoList;
    const newList = [...currentList];
    newList[index] = { ...newList[index], line: value };
    if (onCodeInfoListChange) {
      onCodeInfoListChange(newList);
    }
  }

  // 更新代码内容
  function handleCodeTextChange(index, value) {
    // 直接基于当前显示的数据进行操作
    const currentList = codeInfoList.length > 0 ? codeInfoList : displayCodeInfoList;
    const newList = [...currentList];
    newList[index] = { ...newList[index], codeText: value };
    if (onCodeInfoListChange) {
      onCodeInfoListChange(newList);
    }
  }

  return (
    <div>
      <div style={{ marginTop: '12px' }}>
        {ze('添加相关代码片段：', 'Add source code here:')}
      </div>

      {displayCodeInfoList.map((codeInfo, index) => (
        <div key={index} style={{ marginBottom: '16px', border: '1px solid #d9d9d9', padding: '12px', borderRadius: '6px' }}>
          {/* 堆栈行选择器和控制按钮 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
            gap: '8px',
            minWidth: 0,  // 允许容器收缩
            overflow: 'hidden'  // 防止内容溢出
          }}>
            <Select
              getPopupContainer={() => popupContainer}
              style={{
                flex: 1,
                minWidth: 0  // 允许flex项目收缩到内容宽度以下
              }}
              placeholder={ze('请选择堆栈行', 'Please select stack line')}
              value={codeInfo.line || undefined}
              onChange={(value) => handleLineChange(index, value)}
              options={stackLineOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              // 自定义选中项的显示，截断过长文本
              optionRender={(option) => (
                <div
                  title={option.label}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}
                >
                  {option.label}
                </div>
              )}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleAddCodeInfo(index)}
              title={ze('添加', 'Add')}
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCodeInfo(index)}
              disabled={displayCodeInfoList.length === 1}
              title={ze('删除', 'Delete')}
            />
          </div>

          {/* 代码输入区域 */}
          <div>
            <Input.TextArea
              style={{
                backgroundColor: '#1e1e1e',
                caretColor: '#fff',
                color: '#fff',
              }}
              rows={6}
              value={codeInfo.codeText}
              onChange={(e) => handleCodeTextChange(index, e.target.value)}
              placeholder={ze('请输入该堆栈行相关的代码内容', 'Please enter code content for this stack line')}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const mapStateToProps = (state, router) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(AiIssueAnalysisAddCode);
